import BaseLayout from '@/layout/BaseLayout';
import { Button } from '@shelflix/ui-web/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@shelflix/ui-web/components/ui/card';
import { Form, scrollToError } from '@shelflix/ui-web/components/ui/form';
import { useForm } from 'react-hook-form';

import { useMaterialEditStore, useMaterialFormStore } from '@/screens/MaterialEdit/store';
import Icon from '@shelflix/ui-web/components/Icon';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useHasScopes } from '@/hooks/useHasScopes';
import { queryClient, trpc } from '@/trpcClient';
import { urlToFile } from '@/utils/utils';
import { EditMaterialInputType } from '@shelflix/schemas/material/edit';
import { EDeviceManagerType, ELedPanelTarget, EMaterialStatus } from '@shelflix/schemas/material/enums';
import { CampaignDto } from '@shelflix/trpc/routes/campaign/list/useCase';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';

import { AvailableCampaigns } from '@/components/MaterialPdv/AvailableCampaigns';
import { MaintenanceLogsTable } from '@/components/MaterialPdv/MaintenanceLogsTable';
import { StatusLogsTable } from '@/components/MaterialPdv/StatusLogsTable';
import { TransferLogsTable } from '@/components/MaterialPdv/TransferLogsTable';
import { RestrictedComponent } from '@/layout/RestrictedComponent';
import { usePreventLeave } from '@/utils/usePreventLeave';
import { ScheduleCampaignDto } from '@shelflix/trpc/routes/material/get/useCase';
import { Tabs, TabsList, TabsTrigger } from '@shelflix/ui-web/components/ui/tabs';
import { SwipeableTabs } from '@/components/SwipeableTabs';
import dayjs from 'dayjs';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { CampaignsSection } from '../../components/MaterialPdv/CampaignsSection';
import { DeleteSection } from './DeleteSection';
import { DisplayInfoSection } from './DisplayInfoSection';
import { MaintenanceSection } from './MaintenanceSection';
import { MaterialInfoSection } from './MaterialInfoSection';
import { TransferSection } from './TransferSection';
import { LedControllerSection } from './LedControllerSection';
import { LedPanelSection } from './LedPanelSection';
import { ModemSection } from './ModemSection';

export interface ScheduleCampaign {
  campaign: CampaignDto;
  startDate: string;
  endDate: string;
  isPaused?: boolean;
}

export function MaterialEdit() {
  const { uuid: materialUuid } = useParams<{ uuid: string }>();
  const [isUploading, setIsUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('campaigns');
  const [logsTab, setLogsTab] = useState('status'); // State for nested logs tabs
  const [initialContractName, setInitialContractName] = useState<string | null>(null);
  const [hasContractChanged, setHasContractChanged] = useState(false);
  const [instalationDate, setInstalationDate] = useState<Date | undefined>(undefined);

  // Check which tabs the user has permission to access
  const canViewCampaignsTab = useHasScopes(['material:view']); // Tab visibility controlled by material view permission
  const canEditCampaigns = useHasScopes(['material:campaign']); // Allows scheduling/editing campaigns
  const canViewMaterial = useHasScopes(['material:view']);
  const canEditMaterial = useHasScopes(['material:edit']);
  const canViewMaterialConfig = canViewMaterial; // material:view permission allows viewing config tab
  const canViewTransfer = useHasScopes(['material:transfer']);
  const canViewRemove = useHasScopes(['material:delete']);
  const canViewMaintenance = useHasScopes(['material:status']);
  const canViewLogs = useHasScopes(['material:logs']);

  const {
    isSubmitting,
    isLoadingMaterial,
    contract,
    selectedFacility,
    selectedMaterialType,
    brandUuid,
    defaultCampaign,
    currentCampaign,
    scheduledCampaigns,
    availableCampaign,
    hasCampaignsChangedByUser,
    initializeMaterial,
    setSubmitting,
    openModal,
    setOpenModal,
    addDefaultCampaign,
    removeCurrentCampaign,
    setSelectedCampaign,
    setCurrentCampaign,
    setListScheduledCampaigns,
    setHasCampaignsChangedByUser,
    reset,
  } = useMaterialEditStore();
  usePreventLeave(isUploading);

  const [thumbnail, setThumbnail] = useState<File | null>(null);
  const [deviceManagerType, setDeviceManagerType] = useState<EDeviceManagerType>(EDeviceManagerType.MAGICINFO);
  // FIREBASE and LED_PANEL are the same app-managed device - LED_PANEL just adds panel geometry fields.
  const isFirebaseFamily =
    deviceManagerType === EDeviceManagerType.FIREBASE || deviceManagerType === EDeviceManagerType.LED_PANEL;
  const navigate = useNavigate();

  const { formData, resetForm } = useMaterialFormStore();
  const [isLoaded, setIsLoaded] = useState(false);

  const editMaterialMutation = useMutation(trpc.material.edit.mutationOptions());

  const { data: displays, isPending: isDisplayQueryPending } = useQuery({
    ...trpc.material.listDisplays.queryOptions(),
    enabled: canViewMaterial, // Fetch if user can view material (view permission shows cards, edit permission enables fields)
  });

  // const { data: devices, isPending: isDeviceQueryPending } = useQuery({
  //   ...trpc.material.listDevices.queryOptions(),
  //   enabled: canViewMaterial, // Fetch if user can view material (view permission shows cards, edit permission enables fields)
  // });

  // Campaigns fetch is controlled by shouldFetchCampaigns flag to ensure brandUuid is set before fetching
  const [shouldFetchCampaigns, setShouldFetchCampaigns] = useState(false);
  const {
    data,
    isLoading,
    isError,
    refetch: refetchCampaigns,
  } = useQuery({
    ...trpc.campaign.listAvaliable.queryOptions({
      brandUuid: brandUuid ?? undefined,
      cityIbgeCode: selectedFacility?.city.ibgeCode ?? undefined,
      stateIbgeCode: selectedFacility?.state.ibgeCode ?? undefined,
    }),
    // Only fetch when user has permission AND brandUuid is available AND flag is set
    enabled: shouldFetchCampaigns && canEditCampaigns && !!brandUuid,
    refetchOnWindowFocus: false,
  });

  const getMaterialQuery = trpc.material.get.queryOptions(
    { uuid: materialUuid ?? '' },
    { enabled: !!materialUuid } // Removed dependency on display/device queries - they may be disabled by permissions
  );
  const { data: materialToEdit, isPending } = useQuery(getMaterialQuery);
  const initializedRef = useRef(false);

  const pauseCamapanignMutation = useMutation(trpc.material.pause.mutationOptions());
  const playCampaignNowMutation = useMutation(trpc.material.playCampaign.mutationOptions());

  const form = useForm<
    EditMaterialInputType & {
      cnpj: {
        uuid: string;
        cnpj: string;
        name: string;
      };
      materialTypeUuid: {
        uuid: string;
        name: string;
        brand: {
          name: string;
        };
      };
    }
  >({
    defaultValues: {
      ...formData,
    },
    mode: 'all',
  });

  const campaigns = useMemo(() => {
    return data?.campaigns ?? [];
  }, [data?.campaigns]);

  // Auto-select first visible tab when component mounts or permissions change
  useEffect(() => {
    const tabPermissions = [
      { tab: 'campaigns', hasPermission: canViewCampaignsTab },
      { tab: 'material', hasPermission: canViewMaterialConfig },
      { tab: 'transfer', hasPermission: canViewTransfer },
      { tab: 'remove', hasPermission: canViewRemove },
      { tab: 'maintenance', hasPermission: canViewMaintenance },
      { tab: 'logs', hasPermission: canViewLogs },
    ];

    const firstVisibleTab = tabPermissions.find((t) => t.hasPermission);

    if (firstVisibleTab && activeTab !== firstVisibleTab.tab) {
      setActiveTab(firstVisibleTab.tab);
    }
  }, [canViewCampaignsTab, canViewMaterialConfig, canViewTransfer, canViewRemove, canViewMaintenance, canViewLogs]);

  useEffect(() => {
    if (selectedMaterialType) {
      const newBrandUuid = selectedMaterialType.brand.uuid;
      useMaterialEditStore.setState({ brandUuid: newBrandUuid });
      urlToFile(selectedMaterialType.photoUrl, 'default').then((file) => {
        setThumbnail(file);
      });
      // Set flag to allow campaign fetch now that brandUuid is available
      // The query will run automatically because brandUuid is now set
      if (canEditCampaigns) {
        setShouldFetchCampaigns(true);
      }
    }
  }, [selectedMaterialType, canEditCampaigns]);

  useEffect(() => {
    // Only process campaigns if user has permission to edit them
    if (!canEditCampaigns) return;

    if (!isLoading && !isError && campaigns.length && selectedMaterialType) {
      const defaultUuid = defaultCampaign?.uuid;
      const scheduledUuids = scheduledCampaigns.map((c) => c.campaign.uuid);

      const filteredCampaigns = campaigns.filter(
        (item) => item.uuid !== defaultUuid && !scheduledUuids.includes(item.uuid)
      );

      useMaterialEditStore.setState({ availableCampaign: filteredCampaigns });
    }
  }, [campaigns, isLoading, isError, defaultCampaign?.uuid, scheduledCampaigns, canEditCampaigns]);

  useEffect(() => {
    if (materialToEdit && !isLoadingMaterial) {
      initializeMaterial(materialToEdit);

      // Set device manager type from material data
      if (materialToEdit.deviceManagerType) {
        setDeviceManagerType(materialToEdit.deviceManagerType as EDeviceManagerType);
      }

      if (materialToEdit.installedAt) {
        setInstalationDate(new Date(materialToEdit.installedAt));
      }

      let logoType: string = '';
      urlToFile(materialToEdit.logoUrl, 'default').then((file) => {
        setThumbnail(file);

        logoType = file?.type ?? '';
        form.reset({
          ...materialToEdit,
          device: materialToEdit.device?.uuid ?? undefined,
          materialTypeUuid: materialToEdit.materialType.uuid,
          contractType: contract?.type,
          materialType: logoType,
          cnpj: {
            uuid: materialToEdit.facility.uuid,
            name: materialToEdit.facility.name,
            cnpj: materialToEdit.cnpj,
          },
          brand: materialToEdit.brand,
          currentCampaignUuid: materialToEdit.currentCampaign?.uuid,
          defaultCampaignUuid: materialToEdit.defaultCampaign.uuid,
          facility: materialToEdit.facility.uuid,
          exhibitionPoint: materialToEdit.exhibitionPoint ?? undefined,
          deviceManagerType: materialToEdit.deviceManagerType as EDeviceManagerType,
          firebaseVerificationCode: materialToEdit.firebaseVerificationCode ?? undefined,
          scheduleCampaigns: materialToEdit.scheduleCampaigns.map((item) => {
            return {
              campaignUuid: item.campaign.uuid,
              endDate: item.endDate,
              startDate: item.startDate,
            };
          }),
          ledControllerId: materialToEdit.device?.externalId ?? undefined,
          selectedTags: materialToEdit.tags || [],
          modem: {
            marca: materialToEdit.marca ?? undefined,
            modelo: materialToEdit.modelo ?? undefined,
            identifierNumber: materialToEdit.identifierNumber ?? undefined,
            imei: materialToEdit.imei ?? undefined,
            msisdn: materialToEdit.msisdn ?? undefined,
            monthlyDataLimit: materialToEdit.monthlyDataLimit ?? undefined,
          },
        });
        setIsLoaded(true);
        setSubmitting(false);
      });

      // Store initial contract name for comparison
      if (materialToEdit.contractUrl) {
        urlToFile(materialToEdit.contractUrl, `contract-${materialToEdit.name}`).then((file) => {
          setInitialContractName(file?.name ?? null);
        });
      } else {
        setInitialContractName(null);
      }

      initializedRef.current = true;
    }

    return () => reset();
  }, [materialToEdit?.uuid]);

  // Track contract changes
  useEffect(() => {
    if (contract && initialContractName !== null) {
      setHasContractChanged(contract.name !== initialContractName);
    } else if (contract && initialContractName === null) {
      // New contract added where there was none
      setHasContractChanged(true);
    } else if (!contract && initialContractName !== null) {
      // Contract removed
      setHasContractChanged(true);
    } else {
      setHasContractChanged(false);
    }
  }, [contract, initialContractName]);

  // set current campaign when materialToEdit.currentCampaign change
  useEffect(() => {
    // Only update if currentCampaign exists in materialToEdit and differs from local state
    if (materialToEdit?.currentCampaign?.uuid && materialToEdit.currentCampaign.uuid !== currentCampaign?.uuid) {
      form.setValue('currentCampaignUuid', materialToEdit.currentCampaign.uuid);
      setCurrentCampaign({
        ...materialToEdit.currentCampaign,
        materialCount: 0,
        campaignMedias: materialToEdit.currentCampaign?.medias ?? [],
      });
    } else if (!materialToEdit?.currentCampaign && currentCampaign?.uuid) {
      // If materialToEdit has no currentCampaign but local state does, clear it
      form.setValue('currentCampaignUuid', undefined);
      setCurrentCampaign(null);
    }
  }, [materialToEdit?.currentCampaign]);
  useEffect(() => {
    if (!materialToEdit?.scheduleCampaigns) return;

    const ids = scheduledCampaigns?.map((item) => item.campaign.uuid);
    const materialCampaigns = materialToEdit?.scheduleCampaigns?.map((item) => item.campaign.uuid) ?? [];

    if (ids?.some((i: string) => !materialCampaigns.includes(i))) {
      const schedules =
        materialToEdit.scheduleCampaigns?.map((item: ScheduleCampaignDto) => ({
          campaign: {
            ...item.campaign,
            materialCount: 0,
            campaignMedias: item.campaign.medias ?? [],
          },
          displayFormat: item.campaign.displayFormat,
          isPaused: item.isPaused,
          startDate: item.startDate,
          endDate: item.endDate,
        })) || [];
      setListScheduledCampaigns(schedules);
    }
  }, [materialToEdit?.scheduleCampaigns]);

  // set contract type when contract changes
  useEffect(() => {
    if (contract) {
      form.trigger('contractType');
    }
  }, [contract?.name]);

  // submit form when campaigns changes

  const timeOpenScreen = useRef(0);

  useEffect(() => {
    if (!materialToEdit?.uuid) return;
    const timeout = setTimeout(() => {
      timeOpenScreen.current += 1;
    }, 1500);

    return () => {
      timeOpenScreen.current = 0;
      clearTimeout(timeout);
    };
  }, [materialToEdit?.uuid]);

  // Auto-submit form when campaigns are changed by user action (not during initial load)
  useEffect(() => {
    // Guard: Skip auto-save during initialization (first 1.5s after page load)
    if (timeOpenScreen.current === 0) return;

    if (
      hasCampaignsChangedByUser &&
      !isLoading &&
      !isError &&
      defaultCampaign?.uuid &&
      materialToEdit?.materialType?.uuid &&
      isLoaded &&
      !isSubmitting
    ) {
      onSubmit(form.getValues(), false);
      // Reset the flag after submitting
      setHasCampaignsChangedByUser(false);
    }
  }, [hasCampaignsChangedByUser, currentCampaign, defaultCampaign?.uuid, scheduledCampaigns]);

  const onSubmit = (
    data: EditMaterialInputType & {
      cnpj: {
        uuid: string;
        cnpj: string;
        name: string;
      };
      materialTypeUuid: {
        uuid: string;
        name: string;
        brand: {
          name: string;
        };
      };
      selectedTags?: { uuid: string; name: string }[];
    },
    navigationGoBack?: boolean,
    payloadData?: EditMaterialInputType
  ) => {
    if (editMaterialMutation.isPending) return;
    setSubmitting(true);

    if (!selectedMaterialType) {
      toast.error('Tipo de material não selecionado');
      setSubmitting(false);
      return;
    }

    if (!selectedFacility) {
      toast.error('Estabelecimento não selecionado');
      setSubmitting(false);
      return;
    }

    if (!thumbnail) {
      toast.error('Logo do material não selecionado');
      setSubmitting(false);
      return;
    }

    if (!defaultCampaign) {
      toast.error('Campanha padrão não selecionada');
      setSubmitting(false);
      return;
    }

    // Build payload explicitly with only allowed schema fields (avoid spreading data which includes nested campaign objects with presigned URLs)
    const payload = payloadData ?? {
      ...data,
      uuid: data.uuid,
      name: selectedMaterialType.name,
      materialTypeUuid: data.materialTypeUuid.uuid ?? data.materialTypeUuid,
      cnpj: data.cnpj.cnpj,
      contractType: contract?.type,
      materialType: thumbnail?.type,
      brand: {
        uuid: selectedMaterialType.brand.uuid,
        name: selectedMaterialType.brand.name,
      },
      facility: selectedFacility.uuid,
      defaultCampaignUuid: defaultCampaign?.uuid,
      scheduleCampaigns: scheduledCampaigns.map((item) => {
        return {
          campaignUuid: item.campaign.uuid,
          startDate: item.startDate,
          endDate: item.endDate,
        };
      }),
      currentCampaignUuid: currentCampaign?.uuid,
      exhibitionPoint: !data.exhibitionPoint || data.exhibitionPoint?.trim() === '' ? undefined : data.exhibitionPoint,
      // Display is only relevant for MagicInfo - exclude for Firebase/LED Panel to avoid validation issues
      display: isFirebaseFamily ? undefined : data.display,
      device: data.device,
      deviceManagerType: deviceManagerType,
      firebaseVerificationCode: isFirebaseFamily ? data.firebaseVerificationCode : undefined,
      ledControllerId: data.ledControllerId,
      ledPanelWidthPx: deviceManagerType === EDeviceManagerType.LED_PANEL ? data.ledPanelWidthPx : undefined,
      ledPanelHeightPx: deviceManagerType === EDeviceManagerType.LED_PANEL ? data.ledPanelHeightPx : undefined,
      ledPanelTarget: deviceManagerType === EDeviceManagerType.LED_PANEL ? data.ledPanelTarget : undefined,
      // Offset/output resolution only apply to the TVBOX_HDMI target (no HDMI canvas on ESP32)
      ledPanelOffsetXPx:
        deviceManagerType === EDeviceManagerType.LED_PANEL && data.ledPanelTarget !== ELedPanelTarget.ESP32_HUB75
          ? data.ledPanelOffsetXPx
          : undefined,
      ledPanelOffsetYPx:
        deviceManagerType === EDeviceManagerType.LED_PANEL && data.ledPanelTarget !== ELedPanelTarget.ESP32_HUB75
          ? data.ledPanelOffsetYPx
          : undefined,
      ledPanelOutputResolution:
        deviceManagerType === EDeviceManagerType.LED_PANEL && data.ledPanelTarget !== ELedPanelTarget.ESP32_HUB75
          ? data.ledPanelOutputResolution
          : undefined,
      // Fps only applies to the ESP32_HUB75 target
      ledPanelFps:
        deviceManagerType === EDeviceManagerType.LED_PANEL && data.ledPanelTarget === ELedPanelTarget.ESP32_HUB75
          ? data.ledPanelFps
          : undefined,
      tags: data.selectedTags?.map((tag) => tag.uuid) || [],
      installedAt: data.installedAt ?? undefined,
    };

    editMaterialMutation.mutate(payload, {
      onSuccess: async (response) => {
        await queryClient.invalidateQueries({ queryKey: getMaterialQuery.queryKey });
        try {
          if (contract && response.contractUrl) {
            setIsUploading(true);
            const formData = new FormData();
            Object.entries(response.contractUrl.fields ?? {}).forEach(([key, value]) => {
              formData.append(key, value);
            });
            formData.append('file', contract);

            await fetch(response.contractUrl.url, {
              method: 'POST',
              body: formData,
            });
          }

          // Reset contract change tracking after successful save
          if (contract) {
            setInitialContractName(contract.name);
          }
          setHasContractChanged(false);

          if (navigationGoBack) {
            navigate('/pos', { state: { toastSuccess: true } });
            resetForm();
            reset();
          }

          // Show appropriate toast based on publish status
          if (response.publishPending) {
            toast.success(
              'Alteração salva! A campanha está sendo sincronizada com o dispositivo. Isso pode levar alguns minutos.',
              {
                duration: 8000,
                icon: '⏳',
              }
            );
          } else {
            toast.success('Alteração salva com sucesso', {
              duration: 5000,
            });
          }

          navigate(location.pathname, { replace: true, state: {} });
          setSubmitting(false);
        } catch (error) {
          toast.error(`Erro no upload: ${error.message}`);
        } finally {
          setIsUploading(false);
        }
      },
      onError: async (error) => {
        toast.error(`Erro ao editar material: ${error.message}`);
        // Rollback: Refetch material data to restore previous state after error
        await queryClient.invalidateQueries({ queryKey: getMaterialQuery.queryKey });
        setSubmitting(false);
      },
    });
  };

  const pauseCampaign = (campaignUuid: string, isPaused: boolean) => {
    if (pauseCamapanignMutation.isPending) return;
    setSubmitting(true);
    pauseCamapanignMutation.mutate(
      {
        uuid: materialUuid ?? '',
        pauseCampaignUuid: campaignUuid || '',
        isPaused,
      },
      {
        onSuccess: async (_response) => {
          await queryClient.invalidateQueries({ queryKey: getMaterialQuery.queryKey });
          setSubmitting(false);

          navigate(location.pathname, { replace: true, state: {} });

          // navigate('/pos', { state: { toastSuccess: true } });
        },
        onError: (error) => {
          toast.error(`Erro ao pausar campanha: ${error.message}`);
          setSubmitting(false);
        },
      }
    );
  };

  const onPlay = (campaignUuid: string) => {
    setSubmitting(true);

    const dataForm = form.getValues();
    // Try to find the campaign in scheduled campaigns first, then in available campaigns
    const scheduledCampaign = scheduledCampaigns.find((c) => c.campaign.uuid === campaignUuid);
    const availableCampaignData = availableCampaign.find((c) => c.uuid === campaignUuid);
    const now = dayjs(); // Use current date for immediate playback

    // Determine the endDate: use scheduled campaign's endDate, or the campaign's own endDate, or fallback to far future
    const campaignEndDate = scheduledCampaign?.endDate ?? availableCampaignData?.endDate ?? null;

    if (!selectedMaterialType) {
      toast.error('Tipo de material não selecionado');
      return;
    }

    if (!selectedFacility) {
      toast.error('Estabelecimento não selecionado');
      return;
    }

    if (!thumbnail) {
      toast.error('Logo do material não selecionado');
      return;
    }

    if (!defaultCampaign) {
      toast.error('Campanha padrão não selecionada');
      return;
    }

    if (!materialUuid) {
      toast.error('Material UUID não encontrado');
      return;
    }

    const payload = {
      uuid: materialUuid,
      // Display is only relevant for MagicInfo - exclude for Firebase/LED Panel to avoid validation issues
      display: isFirebaseFamily ? undefined : dataForm.display,
      name: selectedMaterialType.name,
      materialTypeUuid: dataForm.materialTypeUuid.uuid ?? dataForm.materialTypeUuid,
      cnpj: dataForm.cnpj.cnpj,
      contractType: contract?.type,
      materialType: thumbnail.type,
      brand: selectedMaterialType.brand,
      facility: selectedFacility.uuid,
      defaultCampaignUuid: defaultCampaign.uuid,
      scheduleCampaigns: [
        {
          campaignUuid: campaignUuid || '',
          startDate: now.format('YYYY-MM-DD'), // Use current date for startDate
          endDate: campaignEndDate ? dayjs(campaignEndDate).format('YYYY-MM-DD') : '2999-12-31',
        },
      ],
      currentCampaign: scheduledCampaign,
      currentCampaignUuid: campaignUuid,
      exhibitionPoint:
        !dataForm.exhibitionPoint || dataForm.exhibitionPoint?.trim() === '' ? undefined : dataForm.exhibitionPoint,
      device: dataForm.device,
      deviceManagerType: deviceManagerType,
      firebaseVerificationCode: isFirebaseFamily ? dataForm.firebaseVerificationCode : undefined,
    };

    onSubmit(dataForm, false, payload); // Call onSubmit with the payload
    // removeScheduledCampaign(currentCampaign);
  };

  const playCampaignNow = async (campaignUuid: string) => {
    if (playCampaignNowMutation.isPending) return;
    setSubmitting(true);
    onPlay(campaignUuid);

    // const current = scheduledCampaigns.find((c) => c.campaign.uuid === campaignUuid);
    // console.log('playCampaignNow', {
    //   current: currentCampaign?.name,
    //   campaignUuid: current.campaign.name,
    // });
    // playCampaignNowMutation.mutate(
    //   {
    //     materialUuid: materialUuid ?? '',
    //     campaignUuid: campaignUuid || '',
    //   },
    //   {
    //     onSuccess: async (_response) => {
    //       await queryClient.invalidateQueries({ queryKey: getMaterialQuery.queryKey });
    //       setSubmitting(false);
    //       // setCurrentCampaign({
    //       //   ...selectCamp.campaign,
    //       //   uuid: campaignUuid,
    //       //   materialCount: 0,
    //       // });

    //       // setSubmitting(false);

    //       // navigate(location.pathname, {
    //       //   replace: true,
    //       //   state: {},
    //       // });
    //     },
    //     onError: (error) => {
    //       toast.error(`Erro ao tocar campanha: ${error.message}`);
    //     },
    //   }
    // );
    // clearScheduledCampaign(current.campaign);
  };

  const handleRemoveCurrentCampaign = (campaign: CampaignDto) => {
    removeCurrentCampaign(campaign);
  };

  const handleSetDefaultCampaign = (campaign: CampaignDto) => {
    addDefaultCampaign(campaign);
    setOpenModal(null);
  };
  const handleSetSelectedCampaign = (campaign: CampaignDto) => {
    setSelectedCampaign(campaign);
  };

  return (
    <BaseLayout>
      <div className="px-2 md:px-0">
        {isSubmitting ? (
          <div className="inset-0 z-50 flex flex-1 flex-col items-center justify-center gap-4">
            <Loader2 className="text-primary h-16 w-16 animate-spin" />
            <div className="flex flex-col gap-1 text-center">
              <span>Salvando e sincronizando com o dispositivo...</span>
              <span className="text-muted-foreground text-sm">Isso pode levar alguns segundos.</span>
            </div>
          </div>
        ) : isLoadingMaterial || isPending || (canViewMaterial && isDisplayQueryPending) ? (
          <div className="inset-0 z-50 flex flex-1 flex-col items-center justify-center gap-4">
            <Loader2 className="text-primary h-16 w-16 animate-spin" />
            <span className="text-center">{isLoadingMaterial ? 'Carregando dados do material...' : 'Aguarde...'}</span>
          </div>
        ) : (
          <>
            {openModal ? (
              <div className="mt-6 w-screen">
                <AvailableCampaigns
                  header={{
                    title: openModal?.title ?? 'Campanhas disponíveis',
                    icon: openModal?.icon ?? 'hub',
                  }}
                  campaigns={
                    openModal?.type === 'play_now'
                      ? [
                          ...availableCampaign,
                          ...scheduledCampaigns
                            .filter((i) => i.campaign.uuid !== currentCampaign?.uuid)
                            .map((item) => item.campaign),
                        ]
                      : openModal?.type === 'scheduled'
                        ? availableCampaign
                        : [
                            ...availableCampaign,
                            ...scheduledCampaigns
                              .filter((i) => i.campaign.uuid !== currentCampaign?.uuid)
                              .map((item) => item.campaign),
                          ]
                  }
                  type={openModal?.type}
                  handlePlayNow={playCampaignNow}
                  handleSetDefaultCampaign={handleSetDefaultCampaign}
                  handleAddScheduleCampaign={handleSetSelectedCampaign}
                />
              </div>
            ) : (
              <>
                <div className="mb-4 px-4">
                  <h1 className="text-md font-semibold">{materialToEdit ? `${materialToEdit.name}` : ''}</h1>
                  <span className="text-sm">
                    {materialToEdit
                      ? `${materialToEdit.facility.name} - ${materialToEdit.facility.city.name} / ${materialToEdit.facility.state.stateName}`
                      : ''}
                  </span>
                </div>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <div className="w-full overflow-hidden">
                    <TabsList className="ml-4 flex w-full flex-row items-start justify-start md:ml-0">
                      <RestrictedComponent requiredScopes={['material:view']} behavior="hide">
                        <TabsTrigger value="campaigns">Campanhas</TabsTrigger>
                      </RestrictedComponent>
                      {canViewMaterialConfig && <TabsTrigger value="material">Configurações</TabsTrigger>}
                      <RestrictedComponent requiredScopes={['material:transfer']} behavior="hide">
                        <TabsTrigger value="transfer">Transferir</TabsTrigger>
                      </RestrictedComponent>
                      <RestrictedComponent requiredScopes={['material:delete']} behavior="hide">
                        <TabsTrigger value="remove">Remover</TabsTrigger>
                      </RestrictedComponent>
                      <RestrictedComponent requiredScopes={['material:status']} behavior="hide">
                        <TabsTrigger value="maintenance">Manutenção</TabsTrigger>
                      </RestrictedComponent>
                      <RestrictedComponent requiredScopes={['material:logs']} behavior="hide">
                        <TabsTrigger value="logs">Logs</TabsTrigger>
                      </RestrictedComponent>
                    </TabsList>
                  </div>

                  <SwipeableTabs
                    currentTab={activeTab}
                    tabs={['campaigns', 'material', 'transfer', 'remove', 'maintenance', 'logs']}
                    onTabChange={setActiveTab}
                  >
                    {/* Tab: Campaigns */}
                    <RestrictedComponent requiredScopes={['material:view']} behavior="hide">
                      <div className="px-4 md:px-0">
                        <div className="flex w-full flex-col gap-4 sm:gap-8">
                          <section className="w-full">
                            <Card className="bg-background rounded-[10px] py-0">
                              <CardContent className="p-0">
                                <CampaignsSection
                                  defaultCampaign={defaultCampaign}
                                  scheduledCampaigns={scheduledCampaigns}
                                  currentCampaign={currentCampaign}
                                  showCurrentCampaign={true}
                                  onPauseCampaign={pauseCampaign}
                                  onPlayCampaignNow={playCampaignNow}
                                  onRemoveCurrentCampaign={handleRemoveCurrentCampaign}
                                />
                              </CardContent>
                            </Card>
                          </section>
                        </div>
                      </div>
                    </RestrictedComponent>

                    {/* Tab: Material (Configurações) */}
                    <div className="mt-6 px-4 md:px-0">
                      <Form {...form}>
                        <form
                          onSubmit={form.handleSubmit((data) => onSubmit(data, false), scrollToError)}
                          className="w-full"
                        >
                          <div className="flex w-full flex-col gap-4 sm:gap-8">
                            <section className="w-full">
                              <MaterialInfoSection
                                form={form}
                                refetchCampaigns={() => refetchCampaigns()}
                                thumbnail={thumbnail}
                                setThumbnail={setThumbnail}
                                code={materialToEdit?.code ?? ''}
                                materialToEdit={materialToEdit}
                                canEdit={canEditMaterial}
                                deviceManagerType={deviceManagerType}
                                setDeviceManagerType={setDeviceManagerType}
                                installedAt={instalationDate}
                                setInstalledAt={setInstalationDate}
                              />
                            </section>

                            <section className="w-full">
                              <ModemSection form={form} />
                            </section>

                            {!isFirebaseFamily && (
                              <section className="w-full">
                                <Card className="rounded-[10px]">
                                  <CardHeader className="pb-0">
                                    <CardTitle className="text-primary text-2xl leading-5 font-semibold">
                                      Tela
                                    </CardTitle>
                                  </CardHeader>
                                  <CardContent>
                                    {displays?.length && initializedRef.current && (
                                      <DisplayInfoSection displays={displays} form={form} canEdit={canEditMaterial} />
                                    )}
                                  </CardContent>
                                </Card>
                              </section>
                            )}

                            {deviceManagerType === EDeviceManagerType.LED_PANEL && (
                              <section className="w-full">
                                <LedPanelSection form={form} />
                              </section>
                            )}

                            <section>
                              <LedControllerSection form={form} />
                            </section>

                            {/* <section className="w-full">
                              <Card className="rounded-[10px]">
                                <CardHeader className="pb-0">
                                  <CardTitle className="text-primary text-2xl leading-5 font-semibold">
                                    Shelhub
                                  </CardTitle>
                                </CardHeader>
                                <CardContent>
                                  {!!devices?.length && initializedRef.current && (
                                    <ShelhubInfoSection
                                      devices={devices}
                                      currentDeviceUuid={materialToEdit?.device?.uuid}
                                      form={form}
                                      canEdit={canEditMaterial}
                                    />
                                  )}
                                </CardContent>
                              </Card>
                            </section> */}
                          </div>
                          <div className="mt-6 mb-2 flex grow-0 justify-center px-2">
                            <RestrictedComponent requiredScopes={['material:edit']} behavior="hide">
                              <Button
                                type="submit"
                                loading={editMaterialMutation.isPending || isUploading}
                                className="bg-primary hover:bg-primary flex grow cursor-pointer items-center gap-2.5 rounded-[5px] px-5 py-2.5 font-semibold text-white sm:grow-0"
                                disabled={
                                  !thumbnail || isSubmitting || (!form.formState.isDirty && !hasContractChanged)
                                }
                              >
                                <Icon name="label" fill={1} color="#fff" size={16} />
                                <span className="[font-family:'Figtree'] text-sm">Salvar alterações</span>
                              </Button>
                            </RestrictedComponent>
                          </div>
                        </form>
                      </Form>
                    </div>

                    {/* Tab: Transfer */}
                    <RestrictedComponent requiredScopes={['material:transfer']} behavior="hide">
                      <div className="mt-6 px-4 md:px-0">
                        <div className="flex w-full flex-col gap-4 sm:gap-8">
                          <section className="w-full">
                            <TransferSection materialUuid={materialUuid ?? ''} facility={materialToEdit?.facility} />
                          </section>
                        </div>
                      </div>
                    </RestrictedComponent>

                    {/* Tab: Remove */}
                    <RestrictedComponent requiredScopes={['material:delete']} behavior="hide">
                      <div className="mt-6 px-4 md:px-0">
                        <div className="flex w-full flex-col gap-4 sm:gap-8">
                          <section className="w-full">
                            <DeleteSection materialUuid={materialUuid ?? ''} facility={materialToEdit?.facility} />
                          </section>
                        </div>
                      </div>
                    </RestrictedComponent>

                    {/* Tab: Maintenance */}
                    <RestrictedComponent requiredScopes={['material:status']} behavior="hide">
                      <div className="mt-6 px-4 md:px-0">
                        <div className="flex w-full flex-col gap-4 sm:gap-8">
                          <section className="w-full">
                            <MaintenanceSection
                              materialUuid={materialUuid ?? ''}
                              currentStatus={materialToEdit?.status as EMaterialStatus}
                            />
                          </section>
                        </div>
                      </div>
                    </RestrictedComponent>

                    {/* Tab: Logs (with nested tabs) */}
                    <RestrictedComponent requiredScopes={['material:logs']} behavior="hide">
                      <div className="mt-6 md:px-0">
                        <div className="flex w-full flex-col gap-4 sm:gap-8">
                          <section className="w-full">
                            <Card className="!rounded-none !border-x-0 md:!rounded-[10px] md:!border-x">
                              <CardContent className="mt-2 !px-0 md:!px-6">
                                <Tabs value={logsTab} onValueChange={setLogsTab}>
                                  <div className="w-full overflow-hidden px-4">
                                    <TabsList className="flex w-full flex-row items-start justify-start md:ml-0">
                                      <TabsTrigger value="status">Status</TabsTrigger>
                                      <TabsTrigger value="transferencia">Transferência</TabsTrigger>
                                      <TabsTrigger value="manutencao">Manutenção</TabsTrigger>
                                    </TabsList>
                                  </div>
                                  <SwipeableTabs
                                    currentTab={logsTab}
                                    tabs={['status', 'transferencia', 'manutencao']}
                                    onTabChange={setLogsTab}
                                  >
                                    {/* Nested Tab: Status */}
                                    <div className="mt-6 md:px-0">
                                      {materialUuid && <StatusLogsTable materialUuid={materialUuid} />}
                                    </div>
                                    {/* Nested Tab: Transferencia */}
                                    <div className="mt-6 md:px-0">
                                      {materialUuid && <TransferLogsTable materialUuid={materialUuid} />}
                                    </div>
                                    {/* Nested Tab: Manutencao */}
                                    <div className="mt-6 md:px-0">
                                      {materialUuid && <MaintenanceLogsTable materialUuid={materialUuid} />}
                                    </div>
                                  </SwipeableTabs>
                                </Tabs>
                              </CardContent>
                            </Card>
                          </section>
                        </div>
                      </div>
                    </RestrictedComponent>
                  </SwipeableTabs>
                </Tabs>
              </>
            )}
          </>
        )}
      </div>
    </BaseLayout>
  );
}
